import React, { Suspense } from 'react';
import { History } from 'history';
import { Store } from 'redux';

import App from 'layouts/App';
import Loading from 'layouts/Loading';

import { HistoryContext } from 'models/routing';

import useRouter, { CustomRoute } from 'util/hook/useRouter';

interface ChunkErrorBoundaryState {
	hasError: boolean;
}

class ChunkErrorBoundary extends React.Component<
	React.PropsWithChildren<{}>,
	ChunkErrorBoundaryState
> {
	static getDerivedStateFromError(): ChunkErrorBoundaryState {
		return { hasError: true };
	}

	constructor(props: React.PropsWithChildren<{}>) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error: Error): void {
		const isChunkLoadError =
			error.name === 'ChunkLoadError' || error.message.includes('Loading chunk');
		if (isChunkLoadError && window.sessionStorage.getItem('chunkLoadRetried') !== '1') {
			window.sessionStorage.setItem('chunkLoadRetried', '1');
			window.location.reload();
		}
	}

	render(): React.ReactNode {
		const { hasError } = this.state;
		const { children } = this.props;
		if (!hasError) {
			return children;
		}
		return <Loading />;
	}
}

interface RouterProperty {
	routes: CustomRoute;
	history: History;
	store: Store;
}

const Router: React.FC<RouterProperty> = ({ routes, history, store }) => {
	const { loading, component } = useRouter(routes, history, store);

	return (
		<HistoryContext.Provider value={history}>
			<App>
				<ChunkErrorBoundary>
					{loading ? <Loading /> : <Suspense fallback={<Loading />}>{component}</Suspense>}
				</ChunkErrorBoundary>
			</App>
		</HistoryContext.Provider>
	);
};

export default Router;
